import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Added import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import CategoryIcon from '@mui/icons-material/Category'; // Cause
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // High Severity
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Low Severity
import InsightsIcon from '@mui/icons-material/Insights'; // Priority Insights
import SignpostIcon from '@mui/icons-material/Signpost'; // Example Icon for Journey
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // Pain Point / Moderate Severity
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------*/
const getSeverityStyle = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'low':
      return { icon: <InfoOutlinedIcon fontSize="small" />, color: 'info', label: 'Low' };
    case 'moderate':
      return { icon: <WarningAmberIcon fontSize="small" />, color: 'warning', label: 'Moderate' };
    case 'high':
      return { icon: <ErrorOutlineIcon fontSize="small" />, color: 'error', label: 'High' };
    default:
      return {
        icon: <WarningAmberIcon fontSize="small" />,
        color: 'default',
        label: severity || 'N/A',
      };
  }
};

/* -------------------------------------------------------------
 * Robust JSON parser – returns an object or an empty one.
 * -----------------------------------------------------------*/
const safeParse = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data; // Already parsed
  try {
    return JSON.parse(data);
  } catch (err) {
    console.error('JourneyPain results JSON malformed:', err);
    return {};
  }
};

/** -------------------------------------------------------------
 * JourneyPainAnalysis – rich visualisation for journey pain synthesis
 * ------------------------------------------------------------*/
export default function JourneyPainAnalysis({ item }) {
  const [expanded, setExpanded] = React.useState(false);

  if (!item) return null;

  // Parse results safely
  const results = safeParse(item.results);

  /* ───────────────────── Top Pain Points card ────────────────────── */
  const TopPainPoints = () => {
    if (!results.top_pain_points?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WarningAmberIcon color="error" /> Top Pain Points
          </Typography>
          <List dense>
            {results.top_pain_points.map((p, i) => (
              <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                  <Chip label={p.frequency} size="small" variant="outlined" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={p.pain_point}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  /* ───────────────────── Priority Insights card ───────────────────── */
  const PriorityInsights = () => {
    if (!results.priority_insights?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <InsightsIcon color="primary" /> Priority Insights
          </Typography>
          <List dense>
            {results.priority_insights.map((insight, i) => (
              <ListItem key={i} disableGutters>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <InsightsIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <ListItemText primary={insight} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  /* ────────────────────── Stage Prevalence Accordions ─────────────────────── */
  const StagePrevalence = () => {
    if (!results.stage_prevalence?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    };

    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Stage Breakdown
        </Typography>
        {results.stage_prevalence.map((stage, index) => {
          const panelId = `stage-panel-${index}`;
          const severity = getSeverityStyle(stage.dominant_severity);
          return (
            <Accordion
              key={panelId}
              expanded={expanded === panelId}
              onChange={handleChange(panelId)}
              TransitionProps={{ unmountOnExit: true }}
              disableGutters
              sx={{
                '&:before': { display: 'none' },
                border: 1,
                borderColor: 'divider',
                '&:not(:last-child)': { borderBottom: 0 },
                borderRadius: 1,
                mb: 1.5,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 'inherit',
                  borderBottomLeftRadius: expanded === panelId ? 0 : 'inherit',
                  borderBottomRightRadius: expanded === panelId ? 0 : 'inherit',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ width: '100%', pr: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                    {stage.stage}
                  </Typography>
                  <Chip
                    label={`Prevalence: ${stage.prevalence ?? 'N/A'}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={severity.icon}
                    label={`Dominant Severity: ${severity.label}`}
                    size="small"
                    color={severity.color}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack spacing={2}>
                  {/* Common Pain Points */}
                  {stage.common_pain_points?.length > 0 && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'medium',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <WarningAmberIcon fontSize="inherit" color="error" /> Common Pain Points:
                      </Typography>
                      <List dense disablePadding sx={{ pl: 1 }}>
                        {stage.common_pain_points.map((point, idx) => (
                          <ListItem
                            key={`point-${index}-${idx}`}
                            disablePadding
                            sx={{ '&:not(:last-child)': { mb: 0.5 } }}
                          >
                            <ListItemText
                              primary={`• ${point}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Common Underlying Causes */}
                  {stage.common_underlying_causes?.length > 0 && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'medium',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <CategoryIcon fontSize="inherit" color="action" /> Common Underlying Causes:
                      </Typography>
                      <List dense disablePadding sx={{ pl: 1 }}>
                        {stage.common_underlying_causes.map((cause, idx) => (
                          <ListItem
                            key={`cause-${index}-${idx}`}
                            disablePadding
                            sx={{ '&:not(:last-child)': { mb: 0.5 } }}
                          >
                            <ListItemText
                              primary={`• ${cause}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Representative Quotes */}
                  {stage.representative_quotes?.length > 0 && (
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Representative Quotes:
                      </Typography>
                      <List dense disablePadding>
                        {stage.representative_quotes.map((quote, idx) => (
                          <ListItem
                            key={`quote-${index}-${idx}`}
                            disableGutters
                            sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                          >
                            <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                              <FormatQuoteIcon
                                fontSize="small"
                                sx={{ transform: 'scaleX(-1)' }}
                                color="disabled"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={getQuoteText(quote)}
                              primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <AnalysisLayout
      results={results}
      item={{ ...item, name: 'Customer Journey Painpoint Analysis' }}
    >
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <SignpostIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Customer Journey Painpoint Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        <TopPainPoints />
        <PriorityInsights />
        <StagePrevalence />
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
}

JourneyPainAnalysis.propTypes = {
  /** The portfolio-level analysis item object */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    analysis_lens_type: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    qualitative_items_included: PropTypes.arrayOf(PropTypes.string),
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or object
  }).isRequired,
};
